import SignupCguCheckbox, { recordSignupCgu } from "@/components/price/SignupCguCheckbox";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Car, CheckCircle2, Loader2 } from "lucide-react";
import PhotoPickerField from "@/components/price/PhotoPickerField";
import CityPickerInput from "@/components/price/CityPickerInput";
import { PriceScooterIcon } from "@/components/price/PriceIllustrations";
import { savePendingPhoto } from "@/lib/pendingPhoto";
import { useAuth } from "@/context/AuthContext";

/**
 * Price — Driver registration.
 *
 * Ultra-short cartoon/street form: first_name, last_name, email,
 * vehicle_brand_model, license_plate, password, password_confirm.
 * SIRET is sent empty (backend accepts it as a plain str) — the driver
 * can complete their profile later from the dashboard.
 */
export default function DriverRegister() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    vehicle_brand_model: "",
    license_plate: "",
    password: "",
    password_confirm: "",
    price_vehicle_kind: "car", // car | motorcycle | scooter
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  // Ville — même mécanique que côté passager, pour positionner le
  // covoitureur sur la carte publique dès l'inscription.
  const [city, setCity] = useState(null); // { name, lat, lng } | null
  // Code créateur (JEANMARC...) — même mécanique que côté passager.
  const [creatorCode, setCreatorCode] = useState("");
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [creatorError, setCreatorError] = useState("");
  useEffect(() => {
    const code = creatorCode.trim().toUpperCase();
    if (!code) { setCreatorInfo(null); setCreatorError(""); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/price/creator-codes/validate?code=${encodeURIComponent(code)}`);
        setCreatorInfo(data); setCreatorError("");
      } catch (err) {
        setCreatorInfo(null);
        setCreatorError(err?.response?.status === 404 ? "Code inconnu ou désactivé" : "");
      }
    }, 450);
    return () => clearTimeout(t);
  }, [creatorCode]);

  const upd = (k) => (e) => {
    const v = k === "license_plate" ? e.target.value.toUpperCase() : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const [cguOk, setCguOk] = useState(false);
  const [cguVersion, setCguVersion] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const first = form.first_name.trim();
    const last = form.last_name.trim();
    const email = form.email.trim().toLowerCase();
    const vehicle = form.vehicle_brand_model.trim();
    const plate = form.license_plate.trim();

    if (!first) return toast.error("Prénom obligatoire");
    if (!last) return toast.error("Nom obligatoire");
    if (!email || !email.includes("@")) return toast.error("Email invalide");
    if (!vehicle) return toast.error("Véhicule obligatoire");
    if (!plate) return toast.error("Plaque d'immatriculation obligatoire");
    if (form.password.length < 6) return toast.error("Mot de passe : 6 caractères minimum");
    if (form.password !== form.password_confirm) return toast.error("Les mots de passe ne correspondent pas");
    if (!city?.lat) return toast.error("Choisis ta ville dans la liste");
    if (!cguOk) return toast.error("Accepte les Conditions Générales d'Utilisation pour continuer");

    setLoading(true);
    try {
      await api.post("/auth/register-driver", {
        first_name: first,
        last_name: last,
        email,
        siret: "",
        vehicle_brand_model: vehicle,
        license_plate: plate,
        password: form.password,
        phone: "",
        price_vehicle_kind: form.price_vehicle_kind,
        ...(creatorCode ? { creator_code: creatorCode.trim().toUpperCase() } : {}),
        ...(city?.lat ? { city: city.name, city_lat: city.lat, city_lng: city.lng } : {}),
      });
      // Si le covoitureur a choisi une photo (optionnelle), on la met
      // de côté en localStorage — elle sera envoyée à son premier login
      // authentifié après validation admin du dossier.
      if (photoDataUrl) savePendingPhoto(photoDataUrl);
      // Étape documents obligatoire : connexion auto puis envoi du dossier complet.
      try {
        await login(email, form.password);
        await recordSignupCgu(cguVersion);
        nav("/verify-identity?signup=1", { replace: true });
        return;
      } catch { /* connexion impossible → écran de confirmation classique */ }
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="relative min-h-screen text-foreground street-dots"
        style={{ background: "hsl(15 100% 96%)" }}
      >
        <div
          className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center px-6"
          style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="w-full rounded-3xl bg-white p-8 street-border street-shadow text-center">
            <div className="mx-auto grid h-20 w-20 place-items-center rounded-2xl bg-accent rotate-[-4deg] street-border street-shadow-sm">
              <CheckCircle2 className="h-10 w-10 text-accent-foreground" strokeWidth={2.5} />
            </div>
            <h1 className="mt-5 font-[Outfit] text-[28px] font-black leading-tight tracking-tight">
              C&apos;est validé !
            </h1>
            <p className="mt-3 text-sm text-foreground/70 font-medium">
              Ta pré-inscription est bien enregistrée. On te contacte très vite
              pour finaliser ton dossier covoitureur.
            </p>
            <Link
              to="/"
              className="street-press mt-6 inline-flex w-full items-center justify-center rounded-2xl bg-primary py-3.5 font-[Outfit] text-[15px] font-black tracking-tight text-primary-foreground street-border street-shadow-sm"
            >
              Retour à l&apos;accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen text-foreground street-dots"
      style={{ background: "hsl(15 100% 96%)" }}
      data-testid="driver-register-page"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(38, 95%, 62%, 0.42), transparent 70%)" }}
      />

      <div
        className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(24px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          paddingLeft: "max(20px, env(safe-area-inset-left))",
          paddingRight: "max(20px, env(safe-area-inset-right))",
        }}
      >
        <Link
          to="/"
          className="street-press mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-foreground street-border-thin street-shadow-sm"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Retour
        </Link>

        <header className="mt-8">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-accent rotate-[-4deg] street-border street-shadow-sm">
            <Car className="h-8 w-8 text-accent-foreground" strokeWidth={2.5} />
          </div>
          <h1 className="mt-5 font-[Outfit] text-[36px] font-black leading-[1.02] tracking-tight text-foreground">
            Devenir
            <span className="ml-1 inline-block rounded-xl bg-primary px-2 py-0.5 street-border-thin rotate-[-2deg] text-primary-foreground">
              covoitureur
            </span>
          </h1>
          <p className="mt-3 text-sm text-foreground/70 font-medium">
            Pré-inscription rapide. On valide ton profil ensuite.
          </p>
        </header>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Prénom"
              value={form.first_name}
              onChange={upd("first_name")}
              placeholder="Alex"
              autoComplete="given-name"
              testid="driver-register-firstname"
            />
            <Field
              label="Nom"
              value={form.last_name}
              onChange={upd("last_name")}
              placeholder="Martin"
              autoComplete="family-name"
              testid="driver-register-lastname"
            />
          </div>
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={upd("email")}
            placeholder="alex@email.com"
            autoComplete="email"
            testid="driver-register-email"
          />
          {/* Type de véhicule Price — détermine ce qu'on prépose lors
              de la publication d'un trajet (Voiture / Moto / Scooter). */}
          <div className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm">
            <div className="text-[11px] font-black uppercase tracking-widest text-foreground/60 mb-2">
              Type de véhicule
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "car", label: "Voiture" },
                { v: "motorcycle", label: "Moto" },
                { v: "scooter", label: "Scooter" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, price_vehicle_kind: o.v }))}
                  data-testid={`driver-register-kind-${o.v}`}
                  className={
                    "street-press flex flex-col items-center gap-1 rounded-xl py-2 street-border-thin " +
                    (form.price_vehicle_kind === o.v ? "bg-primary text-primary-foreground" : "bg-[#FFF8F1] text-foreground")
                  }
                >
                  {o.v === "car" ? (
                    <Car className="h-5 w-5" strokeWidth={2.5} />
                  ) : (
                    <PriceScooterIcon size={22} />
                  )}
                  <span className="text-[11px] font-black uppercase tracking-widest">{o.label}</span>
                </button>
              ))}
            </div>
            {(form.price_vehicle_kind === "motorcycle" || form.price_vehicle_kind === "scooter") && (
              <p className="mt-2 text-[11px] font-medium text-foreground/60 leading-snug">
                En 2 roues, tu pourras prendre un seul passager derrière. Casque à la charge du passager (option cochable côté passager).
              </p>
            )}
          </div>

          <Field
            label="Véhicule"
            value={form.vehicle_brand_model}
            onChange={upd("vehicle_brand_model")}
            placeholder="Peugeot 308"
            testid="driver-register-vehicle"
          />
          <Field
            label="Plaque d'immatriculation"
            value={form.license_plate}
            onChange={upd("license_plate")}
            placeholder="AA-123-BB"
            testid="driver-register-plate"
            className="font-mono tracking-widest uppercase"
          />
          <Field
            label="Mot de passe"
            type="password"
            value={form.password}
            onChange={upd("password")}
            placeholder="6 caractères min."
            autoComplete="new-password"
            testid="driver-register-password"
          />
          <Field
            label="Confirmer"
            type="password"
            value={form.password_confirm}
            onChange={upd("password_confirm")}
            placeholder="Retape ton mot de passe"
            autoComplete="new-password"
            testid="driver-register-password-confirm"
          />

          {/* Ville — pour positionner le covoitureur sur la carte
              publique dès l'inscription. */}
          <CityPickerInput
            value={city}
            onChange={setCity}
            testid="driver-register-city"
            label="Ta ville"
            required
          />

          {/* Code créateur — optionnel. */}
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-foreground/60">
              Code créateur (optionnel)
            </label>
            <input
              type="text"
              value={creatorCode}
              onChange={(e) => setCreatorCode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 32))}
              placeholder="Ex. JEANMARC"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              data-testid="driver-register-creator-input"
              className="mt-1 w-full rounded-2xl bg-white px-4 py-3 font-[Outfit] font-black tracking-widest uppercase street-border-thin street-shadow-sm outline-none focus:ring-4 focus:ring-primary/25"
              style={{ fontSize: "16px", letterSpacing: "0.15em" }}
            />
            {creatorInfo && (
              <div className="mt-2 rounded-xl bg-emerald-50 px-3 py-2 text-[12px] font-bold text-emerald-900 street-border-thin" data-testid="driver-register-creator-hint">
                🎁 <b>{creatorInfo.owner_name}</b> t'offre <b>{Number(creatorInfo.wallet_bonus_eur).toFixed(0)} €</b> sur ton portefeuille Price
              </div>
            )}
            {creatorError && (
              <div className="mt-2 text-[12px] font-bold text-red-600" data-testid="driver-register-creator-error">
                ❌ {creatorError}
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm">
            <PhotoPickerField
              value={photoDataUrl}
              onChange={setPhotoDataUrl}
              testid="driver-register-photo"
            />
          </div>

          <SignupCguCheckbox checked={cguOk} onChange={setCguOk} onVersion={setCguVersion} testid="driver-register-cgu" />

          <button
            type="submit"
            disabled={loading}
            className="street-press mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-[Outfit] text-[16px] font-black tracking-tight text-primary-foreground street-border street-shadow disabled:opacity-60"
            data-testid="driver-register-submit"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Je m&apos;inscris
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-foreground/70">
          Déjà un compte ?{" "}
          <Link to="/login" className="font-bold text-primary underline-offset-2 hover:underline">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", autoComplete, testid, className = "" }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-bold uppercase tracking-wide text-foreground/80">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={testid}
        className={`w-full rounded-2xl bg-white px-4 py-3.5 font-[Outfit] text-[15px] font-medium text-foreground placeholder:text-foreground/35 street-border-thin street-shadow-sm outline-none focus:ring-4 focus:ring-primary/25 ${className}`}
      />
    </label>
  );
}
